/**
 * Conversational copy for each tenant role, aligned with the permission ladder
 * in RolesAndPermissionsSeeder. Shown beside the role name in RolePicker so
 * people can pick without memorizing permission strings.
 *
 * Ladder order (lowest → highest) matches RolePermissionMatrixTest.
 */
const TENANT_ROLE_ORDER = [
    'Viewer',
    'Content Author',
    'Editor',
    'Admin',
    'Owner',
] as const;

type TenantRoleName = (typeof TENANT_ROLE_ORDER)[number];

const TENANT_ROLE_DESCRIPTIONS: Record<TenantRoleName, string> = {
    Viewer:
        'Can look around the app — devices, scenes, playlists, and the rest — but can’t change anything.',
    'Content Author':
        'Can build and edit scenes, components, and playlists. Devices and their settings stay read-only.',
    Editor:
        'Can manage content and the devices themselves, including tags and what each screen runs.',
    Admin:
        'Full control of the organization: content, devices, locations, and who else is a member.',
    Owner:
        'Same powers as Admin. At least one Owner must remain — they’re the protected account for the org.',
};

const descriptionForRole = (role: string): string => {
    if (role in TENANT_ROLE_DESCRIPTIONS) {
        return TENANT_ROLE_DESCRIPTIONS[role as TenantRoleName];
    }
    return 'Custom role for this organization.';
};

/**
 * Sort assignable role names into the permission ladder order. Unknown names
 * (if any appear later) sort after the known ladder, alphabetically.
 */
const sortTenantRoles = (roles: string[]): string[] => {
    const rank = new Map<string, number>(
        TENANT_ROLE_ORDER.map((name, index) => [name, index]),
    );

    return [...roles].sort((a, b) => {
        const aRank = rank.get(a);
        const bRank = rank.get(b);
        if (aRank !== undefined && bRank !== undefined) return aRank - bRank;
        if (aRank !== undefined) return -1;
        if (bRank !== undefined) return 1;
        return a.localeCompare(b);
    });
};

export { TENANT_ROLE_ORDER, TENANT_ROLE_DESCRIPTIONS, descriptionForRole, sortTenantRoles };
export type { TenantRoleName };
